import { createEntityRef } from '../../../types';
import { isARef } from '../../../utils/entityPropertyUtils';

/** True when a field is likely to contain multi-line content (HTML/markdown/scripts/config). */
export function isExpectedMultilineField(fieldName: string, fieldLabel?: string): boolean {
    const normalizedName = (fieldName ?? '').toString().trim().replace(/^\$\./, '');
    const lowerName = normalizedName.toLowerCase();
    const lowerLabel = fieldLabel?.toLowerCase() || '';

    // Prefer attribute-ref semantics (handles "$.Body", casing, etc.)
    const attrRef = createEntityRef(normalizedName);
    if (isARef(attrRef, 'Body')) return true;
    if (isARef(attrRef, 'Content')) return true;
    if (isARef(attrRef, 'Description')) return true;
    if (isARef(attrRef, 'Script')) return true;
    if (isARef(attrRef, 'Config')) return true;

    const multilineFieldNames = ['body', 'content', 'description', 'script', 'config', 'html', 'markdown', 'md'];
    const multilineFieldLabelKeywords = ['description', 'script', 'body', 'content', 'html', 'markdown'];

    return multilineFieldNames.includes(lowerName) ||
        multilineFieldLabelKeywords.some(keyword => lowerLabel.includes(keyword));
}

/** Returns detailed whitespace issues for display (leading/trailing odd whitespace, newlines, tabs, etc.). */
export function getWhitespaceIssues(value: unknown): string[] {
    const issues: string[] = [];
    if (!value || typeof value !== 'string') return issues;

    if (value.includes('\n')) issues.push('contains newline (\\n)');
    if (value.includes('\r')) issues.push('contains carriage return (\\r)');
    if (value.startsWith('\t')) issues.push('starts with tab');
    if (value.endsWith('\t')) issues.push('ends with tab');

    if (value.length > 0) {
        const firstChar = value[0];
        const lastChar = value[value.length - 1];

        // Non-standard whitespace includes: non-breaking space, zero-width space, en/em spaces, etc.
        const nonStandardWhitespaceRegex = /[\u00A0\u1680\u2000-\u200B\u202F\u205F\u3000\uFEFF]/;
        if (nonStandardWhitespaceRegex.test(firstChar)) {
            const charCode = firstChar.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0');
            issues.push(`starts with non-standard whitespace (U+${charCode})`);
        }
        if (nonStandardWhitespaceRegex.test(lastChar)) {
            const charCode = lastChar.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0');
            issues.push(`ends with non-standard whitespace (U+${charCode})`);
        }
    }

    return issues;
}

export function hasWhitespaceIssues(value: unknown): boolean {
    return getWhitespaceIssues(value).length > 0;
}

export function base64ToBytes(base64: string): Uint8Array {
    const normalized = base64.replace(/\s+/g, '');
    const binaryString = window.atob(normalized);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
    return bytes;
}

export function inferMimeTypeFromFilename(filename: string): string {
    const n = filename.toLowerCase();
    if (n.endsWith('.xml')) return 'application/xml';
    if (n.endsWith('.json')) return 'application/json';
    if (n.endsWith('.txt') || n.endsWith('.log')) return 'text/plain';
    if (n.endsWith('.csv')) return 'text/csv';
    if (n.endsWith('.pdf')) return 'application/pdf';
    if (n.endsWith('.png')) return 'image/png';
    if (n.endsWith('.jpg') || n.endsWith('.jpeg')) return 'image/jpeg';
    if (n.endsWith('.gif')) return 'image/gif';
    if (n.endsWith('.zip')) return 'application/zip';
    return 'application/octet-stream';
}

export function getBestFileFilename(sourceEntity: any): string {
    if (typeof sourceEntity?.FileName === 'string' && sourceEntity.FileName.trim()) return sourceEntity.FileName.trim();
    return 'download.bin';
}

export function getBestFileContentType(sourceEntity: any): string | null {
    const ct =
        (typeof sourceEntity?.ContentType === 'string' && sourceEntity.ContentType.trim())
            ? sourceEntity.ContentType.trim()
            : (typeof sourceEntity?.contentType === 'string' && sourceEntity.contentType.trim())
                ? sourceEntity.contentType.trim()
                : null;
    return ct;
}

export function bytesToBlobPart(bytes: Uint8Array): ArrayBuffer {
    return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

export function getContentBodyValueAndKey(sourceEntity: any): { key: string; value: unknown } {
    const candidates = ['Body', 'body', '$.Body', '$.body'];
    for (const k of candidates) {
        if (sourceEntity && Object.prototype.hasOwnProperty.call(sourceEntity, k)) {
            return { key: k, value: sourceEntity[k] };
        }
    }
    // Fallback: try case-insensitive match (covers schema-driven keys)
    if (sourceEntity && typeof sourceEntity === 'object') {
        const keys = Object.keys(sourceEntity);
        const found = keys.find((k) => (k ?? '').toString().trim().replace(/^\$\./, '').toLowerCase() === 'body');
        if (found) return { key: found, value: (sourceEntity as any)[found] };
    }
    return { key: 'Body', value: undefined };
}

